import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Modal, TouchableOpacity, Platform, ActivityIndicator, Image } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { colores, fuentes } from '../../../styles/variablesCss';
import { BACKEND_URL } from '../../../Config/Const';
import Storage from '../../../utils/Storage';
import { useUser } from '../../../context/UserContext';
import { useCheckoutContext } from '../../../context/CheckoutContext';
import { trackPurchase, clearPendingPurchase } from '../../../utils/analytics';

const PagoBilleteraStaFe = ({ total, qrData, onClose }) => {
  // Obtener los datos de la billetera del contexto
  const { datosBilletera } = useCheckoutContext();
  const [modalVisible, setModalVisible] = useState(true); // Iniciar con el modal abierto
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [pollingLoading, setPollingLoading] = useState(false); // Estado separado para el polling
  const [error, setError] = useState(null);
  const [respuestaAPI, setRespuestaAPI] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  // Estados de pago: pendiente, aprobada, rechazada
  const [estadoPago, setEstadoPago] = useState('pendiente');
  // Countdown: 5 minutos (en segundos)
  const [remainingSeconds, setRemainingSeconds] = useState(300);
  
  // Obtener datos del usuario
  const { mail, codUsu } = useUser();

  // Función para la consulta inicial (generar QR)
  const generarCodigoQR = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let emailUsuario = mail;
      let codigoUsuario = codUsu;
      
      // Obtener datos de autenticación si no están en el contexto
      if (!emailUsuario || !codigoUsuario) {
        if (Platform.OS === 'web') {
          emailUsuario = localStorage.getItem('mail') || '';
          codigoUsuario = localStorage.getItem('codUsu') || '';
        } else {
          emailUsuario = await Storage.getItem('mail') || '';
          codigoUsuario = await Storage.getItem('codUsu') || '';
        }
      }
      
      // Crear query params
      const queryParams = new URLSearchParams();
      if (emailUsuario) queryParams.append('mail', emailUsuario);
      if (codigoUsuario) queryParams.append('codUsu', codigoUsuario);
      
      // Agregar parámetros adicionales que la API pueda necesitar
      queryParams.append('monto', parseFloat(total).toFixed(2));
      queryParams.append('descripcion', 'Pago en Pingüino');
      
      // Agregar oid y caja si están disponibles en datosBilletera
      if (datosBilletera?.oid) queryParams.append('oid', datosBilletera.oid);
      if (datosBilletera?.cajaId) queryParams.append('caja', datosBilletera.cajaId);
      
      // Realizar la llamada a la API
      const response = await fetch(`${BACKEND_URL}/rest/fin/pagoBilletera?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      
      // Procesar la respuesta
      if (data && data.response && data.response.dsResultado) {
        setRespuestaAPI(data.response.dsResultado);
        
        // Si la API devuelve una URL para el QR, usarla
        if (data.response.dsResultado.urlQR) {
          setQrCodeUrl(data.response.dsResultado.urlQR);
        } else {
          // Si no, generamos un QR localmente
          generarQRLocal();
        }
      } else {
        setError('Error al obtener datos de pago');
        // Generar QR local como fallback
        generarQRLocal();
      }
    } catch (err) {
      setError(`Error de conexión: ${err.message}`);
      // Generar QR local como fallback
      generarQRLocal();
    } finally {
      setLoading(false);
    }
  };

  // Cancelar compra: DELETE a la misma API con parámetros requeridos
  const cancelarCompra = async () => {
    try {
      // Detener polling
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }

      // Obtener credenciales
      let emailUsuario = mail;
      let codigoUsuario = codUsu;
      if (!emailUsuario || !codigoUsuario) {
        if (Platform.OS === 'web') {
          emailUsuario = localStorage.getItem('mail') || '';
          codigoUsuario = localStorage.getItem('codUsu') || '';
        } else {
          emailUsuario = await Storage.getItem('mail') || '';
          codigoUsuario = await Storage.getItem('codUsu') || '';
        }
      }

      const query = new URLSearchParams();
      if (emailUsuario) query.append('mail', emailUsuario);
      if (codigoUsuario) query.append('codUsu', codigoUsuario);
      
      // Agregar parámetros adicionales que la API pueda necesitar
      query.append('monto', parseFloat(total).toFixed(2));
      query.append('descripcion', 'Pago en Pingüino');
      
      // Agregar oid y caja si están disponibles en datosBilletera
      if (datosBilletera?.oid) query.append('oid', datosBilletera.oid);
      if (datosBilletera?.cajaId) query.append('caja', datosBilletera.cajaId);
      if (datosBilletera?.ordenId) query.append('ordenId', datosBilletera.ordenId);

      await fetch(`${BACKEND_URL}/rest/fin/pagoBilletera?${query.toString()}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });
    } catch (e) {
      // Solo registramos el error localmente; el cierre del modal sigue
      
    }
  };

  // Función para verificar el estado del pago (sin regenerar QR)
  const verificarEstadoPago = async () => {
    try {
      // Mostrar indicador sutil de polling
      setPollingLoading(true);
      setError(null);
      
      let emailUsuario = mail;
      let codigoUsuario = codUsu;
      
      // Obtener datos de autenticación si no están en el contexto
      if (!emailUsuario || !codigoUsuario) {
        if (Platform.OS === 'web') {
          emailUsuario = localStorage.getItem('mail') || '';
          codigoUsuario = localStorage.getItem('codUsu') || '';
        } else {
          emailUsuario = await Storage.getItem('mail') || '';
          codigoUsuario = await Storage.getItem('codUsu') || '';
        }
      }
      
      // Crear query params
      const queryParams = new URLSearchParams();
      if (emailUsuario) queryParams.append('mail', emailUsuario);
      if (codigoUsuario) queryParams.append('codUsu', codigoUsuario);
      
      // Agregar oid y caja si están disponibles en datosBilletera
      if (datosBilletera?.oid) queryParams.append('oid', datosBilletera.oid);
      if (datosBilletera?.cajaId) queryParams.append('caja', datosBilletera.cajaId);
      
      // Realizar la llamada a la API para verificar estado - Endpoint diferente que NO regenera QR
      const response = await fetch(`${BACKEND_URL}/rest/fin/pagoBilletera?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      
      // Solo procesamos el estado del pago, nunca regeneramos el QR durante el polling
      if (data && data.response && data.response.dsResultado) {
        
        // Verificar el estado del pago en el mensaje
        if (data.response.dsResultado.resultado && 
            data.response.dsResultado.resultado[0] && 
            data.response.dsResultado.resultado[0].mensaje) {
          
          const mensajeEstado = data.response.dsResultado.resultado[0].mensaje.toLowerCase();
          
          if (mensajeEstado === 'aprobada') {
            trackPurchase();
            setEstadoPago('aprobada');
            // Finalizar polling
            if (pollingInterval) {
              clearInterval(pollingInterval);
              setPollingInterval(null);
            }
          } else if (mensajeEstado === 'rechazada') {
            clearPendingPurchase();
            setEstadoPago('rechazada');
            // Finalizar polling
            if (pollingInterval) {
              clearInterval(pollingInterval);
              setPollingInterval(null);
            }
          }
          // Si es 'pendiente', continuamos con el polling
        }
      } else {
        // Solo registramos el error, pero NO regeneramos el QR
        setError('Error al verificar estado del pago');
      }
    } catch (err) {
      // Solo registramos el error, pero NO regeneramos el QR
      setError(`Error de conexión: ${err.message}`);
    } finally {
      setLoading(false);
      setPollingLoading(false);
    }
  };
  
  // Generar QR local como fallback o usar el QR de la API si está disponible
  const generarQRLocal = () => {
    // Si tenemos el QR de la API (de datosBilletera o de qrData), usarlo
    if (datosBilletera?.qrCaja) {
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(datosBilletera.qrCaja)}`;
      setQrCodeUrl(qrUrl);
      return;
    }
  };
  
  // Iniciar el polling cuando se monte el componente
  useEffect(() => {
    // Realizar la consulta inicial para generar el QR
    if (datosBilletera?.qrCaja) {
      generarQRLocal();
    } else {
      generarCodigoQR();
    }
    
    // Configurar el intervalo de polling (cada 5 segundos)
    const interval = setInterval(() => {
      // Continuar polling mientras estemos en estado pendiente
      if (estadoPago === 'pendiente') {
        verificarEstadoPago();
      }
    }, 3000);
    
    // Guardar la referencia al intervalo
    setPollingInterval(interval);
    
    // Limpiar el intervalo cuando el componente se desmonte
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [datosBilletera, estadoPago]); // Solo depende de datosBilletera ya que el modal está siempre abierto

  // Reiniciar el contador cuando se abre el modal o cambia el QR
  useEffect(() => {
    if (!modalVisible) return;
    setRemainingSeconds(300); // 5 minutos
  }, [modalVisible, qrCodeUrl]);

  // Ticker de cuenta regresiva
  useEffect(() => {
    if (!modalVisible) return;
    if (estadoPago !== 'pendiente') return;
    if (remainingSeconds <= 0) {
      // Llamar al DELETE cuando se termina el tiempo
      cancelarCompra().then(() => {
        clearPendingPurchase();
        setModalVisible(false);
        if (typeof onClose === 'function') onClose();
      });
      return;
    }
    const tick = setInterval(() => {
      setRemainingSeconds((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(tick);
  }, [modalVisible, remainingSeconds, estadoPago]);

  const formatMMSS = (totalSeconds) => {
    const m = Math.floor(totalSeconds / 60)
      .toString()
      .padStart(2, '0');
    const s = (totalSeconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  return (
    <View style={styles.container}> 
      {/* Modal para mostrar el código QR */}
      <Modal
        visible={modalVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => {
          clearPendingPurchase();
          setModalVisible(false);
          if (typeof onClose === 'function') onClose();
        }}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitulo}>Código QR para pago</Text>
              <Text style={{ fontFamily: fuentes.bold, color: colores.textoPrincipal }}>
                {formatMMSS(remainingSeconds)}
              </Text>
              {estadoPago === 'aprobada' && (
                <View style={styles.estadoCompletado}>
                  <MaterialCommunityIcons name="check-circle" size={24} color={colores.verde || '#28a745'} />
                  <Text style={styles.estadoCompletadoTexto}>Pago aprobado</Text>
                </View>
              )}
            </View>
            
            {loading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={colores.rojo} />
                <Text style={styles.loadingText}>Generando código QR...</Text>
              </View>
            ) : estadoPago === 'aprobada' ? (
              <View style={styles.completadoContainer}>
                <MaterialCommunityIcons name="check-circle-outline" size={80} color={colores.verde || '#28a745'} />
                <Text style={styles.completadoTexto}>Su pago ha sido aprobado</Text>
              </View>
            ) : estadoPago === 'rechazada' ? (
              <View style={styles.errorContainer}>
                <MaterialCommunityIcons name="close-circle-outline" size={80} color={colores.error} />
                <Text style={styles.errorTextoGrande}>Su pago ha sido rechazado</Text>
              </View>
            ) : (
              <View style={styles.qrContainer}>
                {qrCodeUrl ? (
                  <Image
                    source={{ uri: qrCodeUrl }}
                    accessibilityLabel="Código QR para pago"
                    style={{ width: 320, height: 320 }}
                    resizeMode="contain"
                  />
                ) : (
                  <View style={styles.qrImage}>
                    <Text>QR Code Image</Text>
                  </View>
                )}
              </View>
            )}
            
            {respuestaAPI && respuestaAPI.instrucciones && (
              <View style={styles.instruccionesContainer}>
                <Text style={styles.instruccionesTitulo}>Instrucciones:</Text>
                <Text style={styles.instruccionesTexto}>{respuestaAPI.instrucciones}</Text>
              </View>
            )}
            
            <Text style={styles.qrInstrucciones}>
              Escanea este código QR con la aplicación Billetera Santa Fe para realizar tu pago.
            </Text>
            
            <Text style={styles.qrMonto}>
              Monto: $ {parseFloat(total).toFixed(2)}
            </Text>
            
            <TouchableOpacity
              style={styles.botonCerrar}
              onPress={async () => {
                await cancelarCompra();
                setModalVisible(false);
                if (typeof onClose === 'function') onClose(estadoPago);
              }}
            >
              <Text style={styles.botonCerrarTexto}>Cancelar Pago</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  errorContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    marginVertical: 10,
  },
  errorTextoGrande: {
    marginTop: 15,
    fontSize: 16,
    fontFamily: fuentes.bold,
    color: colores.error,
    textAlign: 'center',
  },
  pollingIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginVertical: 15,
    padding: 10,
    backgroundColor: 'rgba(253, 126, 20, 0.05)', // Muy sutil para no distraer
  },
  pollingIndicatorText: {
    marginLeft: 10,
    color: colores.textoSecundario, // Color más suave que no distraiga
    fontSize: 14,
    fontFamily: fuentes.regular,
  },
  modalHeader: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
  },
  estadoCompletado: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(40, 167, 69, 0.1)',
    paddingHorizontal: 10,
    paddingVertical: 5,
  },
  estadoCompletadoTexto: {
    color: colores.verde || '#28a745',
    marginLeft: 5,
    fontSize: 14,
    fontFamily: fuentes.medium,
  },
  completadoContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    marginVertical: 10,
  },
  completadoTexto: {
    marginTop: 15,
    fontSize: 16,
    fontFamily: fuentes.bold,
    color: colores.verde || '#28a745',
    textAlign: 'center',
  },
  estadoContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 5,
    borderTopWidth: 1,
    borderTopColor: colores.borde,
    marginTop: 10,
    paddingTop: 10,
  },
  estadoValor: {
    fontSize: 14,
    fontFamily: fuentes.bold,
  },
  estadoCompletadoValor: {
    color: colores.verde || '#28a745',
  },
  estadoErrorValor: {
    color: colores.error,
  },
  estadoPendienteValor: {
    color: colores.naranja || '#fd7e14',
  },
  estadoIndicatorCompletado: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginVertical: 15,
    padding: 10,
    backgroundColor: 'rgba(40, 167, 69, 0.1)',
  },
  estadoIndicatorTextoCompletado: {
    marginLeft: 10,
    color: colores.verde || '#28a745',
    fontSize: 14,
    fontFamily: fuentes.bold,
  },
  estadoIndicatorError: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginVertical: 15,
    padding: 10,
    backgroundColor: 'rgba(220, 53, 69, 0.1)',
  },
  estadoIndicatorTextoError: {
    marginLeft: 10,
    color: colores.error,
    fontSize: 14,
    fontFamily: fuentes.bold,
  },
  estadoIndicatorPendiente: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginVertical: 15,
    padding: 10,
    backgroundColor: 'rgba(253, 126, 20, 0.1)',
  },
  estadoIndicatorTextoPendiente: {
    marginLeft: 10,
    color: colores.naranja || '#fd7e14',
    fontSize: 14,
    fontFamily: fuentes.medium,
  },
  loadingIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginVertical: 15,
    padding: 10,
    backgroundColor: colores.fondoGris,
  },
  loadingIndicatorText: {
    marginLeft: 10,
    color: colores.textoSecundario,
    fontSize: 14,
    fontFamily: fuentes.medium,
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  loadingText: {
    marginTop: 10,
    color: colores.textoSecundario,
    fontSize: 14,
  },
  errorTexto: {
    color: colores.error,
    marginTop: 10,
    textAlign: 'center',
  },
  botonDeshabilitado: {
    opacity: 0.7,
    backgroundColor: colores.grisOscuro,
  },
  datosPagoContainer: {
    backgroundColor: colores.fondoGris,
    padding: 10,
    marginBottom: 15,
  },
  datosPagoTitulo: {
    fontSize: 16,
    fontFamily: fuentes.semiBold,
    color: colores.textoPrincipal,
    marginBottom: 10,
  },
  datoFila: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 5,
  },
  datoLabel: {
    fontSize: 14,
    color: colores.textoSecundario,
    fontFamily: fuentes.medium,
  },
  datoValor: {
    fontSize: 14,
    color: colores.textoPrincipal,
    fontFamily: fuentes.bold,
  },
  instruccionesContainer: {
    marginTop: 15,
    padding: 10,
    backgroundColor: colores.fondoGris,
    width: '100%',
  },
  instruccionesTitulo: {
    fontSize: 16,
    fontFamily: fuentes.bold,
    color: colores.textoPrincipal,
    marginBottom: 5,
  },
  instruccionesTexto: {
    fontSize: 14,
    color: colores.textoSecundario,
    lineHeight: 20,
  },
  container: {
    width: '100%',
    marginTop: 10,
  },
  titulo: {
    fontSize: 18,
    fontFamily: fuentes.semiBold,
    color: colores.textoPrincipal,
    marginBottom: 15,
  },
  infoContainer: {
    backgroundColor: colores.fondoClaro,
    padding: 15,

  },
  infoTexto: {
    fontSize: 14,
    color: colores.textoSecundario,
    marginBottom: 15,
  },
  montoTexto: {
    fontSize: 16,
    color: colores.textoPrincipal,
    marginBottom: 20,
  },
  montoValor: {
    fontFamily: fuentes.bold,
    color: colores.rojo,
  },
  botonQR: {
    backgroundColor: colores.rojo,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
  },
  botonQRTexto: {
    color: colores.blanco,
    fontFamily: fuentes.semiBold,
    marginLeft: 10,
    fontSize: 16,
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    padding: 20,
  },
  modalContent: {
    backgroundColor: colores.blanco,
    padding: 20,
    width: Platform.OS === 'web' ? '80%' : '90%',
    maxWidth: 400,
    alignItems: 'center',
  },
  modalTitulo: {
    fontSize: 20,
    fontFamily: fuentes.bold,
    color: colores.textoPrincipal,
    textAlign: 'center',
  },
  qrContainer: {
    backgroundColor: colores.blanco,
    padding: 10,
    marginBottom: 20,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 2px 8px rgba(0,0,0,0.25)' }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.25,
          shadowRadius: 3.84,
          elevation: 5,
        }
    ),
  },
  qrImage: {
    width: 320,
    height: 320,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
  },
  qrInstrucciones: {
    textAlign: 'center',
    color: colores.textoSecundario,
    marginBottom: 15,
    fontSize: 14,
  },
  qrMonto: {
    fontSize: 18,
    fontFamily: fuentes.bold,
    color: colores.rojo,
    marginBottom: 20,
  },
  botonCerrar: {
    backgroundColor: colores.fondoGris,
    paddingVertical: 10,
    paddingHorizontal: 20,
  },
  botonCerrarTexto: { 
    color: colores.textoPrincipal,
    fontFamily: fuentes.semiBold,
  },
});

export default PagoBilleteraStaFe;
